/**
 * Home Meal Prep Calculator (aka Food Prep Resource) — helps a household
 * plan a freeze-dry stockpile that actually feeds them.
 *
 * Flow:
 *   1. Add each household member (name, age, gender, activity level,
 *      + optional pregnancy / breastfeeding boost). We look up their
 *      target daily calories from the USDA Dietary Guidelines 2020-2025
 *      reference table so operators don't need to run BMR math by hand.
 *   2. Show the daily calorie total for the whole household, split by
 *      MyPlate food groups (grains, protein, veg, fruit, dairy).
 *   3. Pick a target coverage window (30 / 60 / 90 / 180 / 365 days) and
 *      a freeze-dryer size — the calc returns total meals, total wet-food
 *      kg to source, number of batches, and a rough production timeline.
 *
 * All math runs client-side. No API call. Every input is a controlled
 * component with a data-testid.
 */

import React, { useContext, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Users, Plus, Trash2, Utensils, Package, Clock, Sparkles, Printer,
  AlertTriangle, ShoppingCart, DollarSign, ChefHat, Loader2, FileText,
  RefreshCw, Shuffle, CheckCircle2, ExternalLink, Save, FolderOpen,
  MapPin,
} from 'lucide-react';
import ToolFeedbackButton from './ToolFeedbackButton';
import AIProgressIndicator from './AIProgressIndicator';
import RecipeCardModal from './RecipeCardModal';
import ToolDisclaimer from './ToolDisclaimer';
import SubstitutionModal from './SubstitutionModal';
import PlanBundlePdf, { PlanSectionPdfButtons } from './PlanBundlePdf';
import ProductionCalendar from './ProductionCalendar';
import ToolNavBar from './ToolNavBar';
import MealPlanBooklet from './MealPlanBooklet';

// ---------------- USDA Dietary Guidelines 2020-2025 (average kcal/day) ---
// Rows are inclusive age ranges. Values inside each row: sedentary /
// moderately active / active. Numbers come from the DGA appendix 2.
const KCAL_TABLE = {
  female: [
    { ageMax: 3,  sed: 1000, mod: 1200, act: 1400 },
    { ageMax: 8,  sed: 1200, mod: 1500, act: 1800 },
    { ageMax: 13, sed: 1600, mod: 1800, act: 2200 },
    { ageMax: 18, sed: 1800, mod: 2000, act: 2400 },
    { ageMax: 30, sed: 1900, mod: 2100, act: 2400 },
    { ageMax: 50, sed: 1800, mod: 2000, act: 2200 },
    { ageMax: 120,sed: 1600, mod: 1800, act: 2100 },
  ],
  male: [
    { ageMax: 3,  sed: 1000, mod: 1200, act: 1400 },
    { ageMax: 8,  sed: 1400, mod: 1600, act: 2000 },
    { ageMax: 13, sed: 1800, mod: 2200, act: 2600 },
    { ageMax: 18, sed: 2400, mod: 2800, act: 3200 },
    { ageMax: 30, sed: 2400, mod: 2700, act: 3000 },
    { ageMax: 50, sed: 2200, mod: 2500, act: 2900 },
    { ageMax: 120,sed: 2000, mod: 2300, act: 2600 },
  ],
};

const ACTIVITY_LEVELS = [
  { value: 'sed',     label: 'Sedentary',          bonus: 0    },
  { value: 'mod',     label: 'Moderately Active',  bonus: 0    },
  { value: 'act',     label: 'Active',             bonus: 0    },
  { value: 'very',    label: 'Very Active',        bonus: 300  },
  { value: 'extreme', label: 'Extremely Active',   bonus: 600  },
  { value: 'olympic', label: 'Olympic-tier',       bonus: 1000 },
];

// Extra kcal for pregnancy / lactation from ACOG + DGA guidance.
const PREGNANCY_ADD    = 340;
const BREASTFEEDING_ADD = 450;

// MyPlate — % of daily calories per food group + typical fresh weight
// per 100 kcal, so we can back into fresh food kg per household.
const FOOD_GROUPS = [
  { key: 'grains',   label: 'Grains',     pct: 0.30, gPer100Kcal: 30 },  // rice/pasta cooked ≈ 130 kcal/100g
  { key: 'protein',  label: 'Protein',    pct: 0.20, gPer100Kcal: 55 },  // meat/legumes ≈ 180 kcal/100g
  { key: 'veg',      label: 'Vegetables', pct: 0.20, gPer100Kcal: 300 }, // mixed veg ≈ 33 kcal/100g
  { key: 'fruit',    label: 'Fruit',      pct: 0.10, gPer100Kcal: 200 }, // fresh fruit ≈ 50 kcal/100g
  { key: 'dairy',    label: 'Dairy',      pct: 0.15, gPer100Kcal: 150 }, // milk/yogurt ≈ 65 kcal/100g
  { key: 'fatsOils', label: 'Fats / Oils',pct: 0.05, gPer100Kcal: 12  }, // oil ≈ 880 kcal/100g
];

// Freeze-dryer capacities — approximate Harvest Right sheet capacities
// per batch (fresh wet weight) + typical cycle length. Numbers err on
// the conservative side so plans don't oversell the machine.
const MACHINES = [
  { value: 'small',  label: 'Small (4-5 lb / ~2 kg)',   capacityKg: 2.0, cycleH: 24 },
  { value: 'medium', label: 'Medium (7-10 lb / ~4 kg)', capacityKg: 4.0, cycleH: 28 },
  { value: 'large',  label: 'Large (16-25 lb / ~10 kg)',capacityKg: 10.0, cycleH: 32 },
  { value: 'xl',     label: 'Pro / XL (35+ lb / ~18 kg)', capacityKg: 18.0, cycleH: 36 },
];

const DEFAULT_MEMBERS = () => [
  { id: crypto.randomUUID(), name: 'Adult 1', age: 35, gender: 'male',   activity: 'mod', pregnant: false, breastfeeding: false },
  { id: crypto.randomUUID(), name: 'Adult 2', age: 34, gender: 'female', activity: 'mod', pregnant: false, breastfeeding: false },
];

function kcalFor(member) {
  const table = KCAL_TABLE[member.gender] || KCAL_TABLE.female;
  const age   = Number(member.age) || 0;
  const row   = table.find((r) => age <= r.ageMax) || table[table.length - 1];
  const activity = member.activity || 'mod';
  const level    = ACTIVITY_LEVELS.find((a) => a.value === activity);
  // very / extreme / olympic aren't in the base DGA table — start from
  // the "active" row and add a fixed bonus for the harder workload.
  let kcal;
  if (level && level.bonus > 0) {
    kcal = row.act + level.bonus;
  } else {
    kcal = row[activity] || row.mod;
  }
  if (member.gender === 'female' && member.pregnant)     kcal += PREGNANCY_ADD;
  if (member.gender === 'female' && member.breastfeeding) kcal += BREASTFEEDING_ADD;
  return kcal;
}

export default function FoodPrepResource({ embedded = false }) {
  const { API, user } = useContext(AuthContext);
  const [members, setMembers]         = useState(DEFAULT_MEMBERS);
  const [days, setDays]               = useState(90);
  const [machineKey, setMachineKey]   = useState('medium');
  const [mealsPerDay, setMealsPerDay] = useState(3);

  // AI planner state
  const [pantry, setPantry]           = useState('');
  const [preferences, setPreferences] = useState('');
  const [aiLoading, setAiLoading]     = useState(false);
  const [plan, setPlan]               = useState(null);
  const [activeMeal, setActiveMeal]   = useState(null);   // recipe-card modal target
  const [swapRow, setSwapRow]         = useState(null);   // shopping-list row → swap modal
  const [verifyingIdx, setVerifyingIdx] = useState(-1);   // which row is refreshing price

  // Currency + region — feed AI prompts + label $ figures in the UI.
  const [currency, setCurrency] = useState('USD');
  const [region, setRegion]     = useState('');

  // Saved plans (persist a full AI output for later reload).
  const [savedPlans, setSavedPlans]       = useState([]);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [saveName, setSaveName]           = useState('');
  const [savingPlan, setSavingPlan]       = useState(false);

  const currencySymbol = currency === 'CAD' ? 'C$' : '$';

  // Load saved plans on mount + whenever we save/delete.
  const refreshSavedPlans = React.useCallback(async () => {
    if (!API || !user) return;
    try {
      const { data } = await axios.get(`${API}/tools/meal-plan-ai/saved`);
      setSavedPlans(data.plans || []);
    } catch { /* silent — non-critical */ }
  }, [API, user]);

  React.useEffect(() => { refreshSavedPlans(); }, [refreshSavedPlans]);

  // Enrich members with their computed kcal so the roster PDF can print
  // the same numbers the operator sees on-screen without re-running kcalFor.
  const membersForPdf = useMemo(
    () => members.map((m) => ({ ...m, __kcal: kcalFor(m) })),
    [members],
  );

  // Recompute plan.totals from the shopping_list rows — used every time
  // we mutate a row (verify price, apply swap). Keeps the "Savings" tiles
  // honest without a fresh AI round-trip.
  const recomputeTotals = (rows) => {
    const raw = rows.reduce((s, r) => s + (Number(r.raw_cost_usd) || 0), 0);
    const rte = rows.reduce((s, r) => s + (Number(r.ready_meal_equivalent_usd) || 0), 0);
    const savings = rte - raw;
    const pct = rte > 0 ? (savings / rte) * 100 : 0;
    return {
      raw_cost_usd:        Number(raw.toFixed(2)),
      ready_meal_cost_usd: Number(rte.toFixed(2)),
      savings_usd:         Number(savings.toFixed(2)),
      savings_pct:         Number(pct.toFixed(1)),
    };
  };

  const patchShoppingRow = (i, patch, extraSources = []) => {
    setPlan((prev) => {
      if (!prev) return prev;
      const rows = prev.shopping_list.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
      const merged = [...(prev.sources || [])];
      for (const u of extraSources) if (u && !merged.includes(u)) merged.push(u);
      return { ...prev, shopping_list: rows, sources: merged, totals: recomputeTotals(rows) };
    });
  };

  // ------------------ Per-row live-price verify -------------------
  const verifyRowPrice = async (i) => {
    if (!plan?.shopping_list?.[i]) return;
    const row = plan.shopping_list[i];
    setVerifyingIdx(i);
    try {
      const { data } = await axios.post(`${API}/tools/meal-plan-ai/verify-price`, {
        ingredient:  row.ingredient,
        quantity_kg: Number(row.quantity_kg) || 0,
        currency,
        region,
      });
      if (!data?.price_per_kg) {
        toast.warning('Live price lookup returned nothing — kept the original estimate.');
        return;
      }
      patchShoppingRow(i, {
        raw_cost_usd: data.line_total_usd || (data.price_per_kg * (Number(row.quantity_kg) || 0)),
        __verified: {
          product_name:   data.product_name,
          unit_size:      data.unit_size,
          unit_price_usd: data.unit_price_usd,
          price_per_kg:   data.price_per_kg,
          sources:        data.sources || [],
          notes:          data.notes,
          fetched_at:     data.fetched_at,
        },
      }, data.sources || []);
      toast.success(`Verified ${row.ingredient} — savings recalculated.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Live price lookup failed');
    } finally {
      setVerifyingIdx(-1);
    }
  };

  const applySwap = (rowIdx, swap) => {
    if (!plan?.shopping_list?.[rowIdx]) return;
    const row = plan.shopping_list[rowIdx];
    // Preserve the ready-meal equivalent so savings math still tells the
    // "vs. Mountain House" story after the swap.
    patchShoppingRow(rowIdx, {
      ingredient:   swap.ingredient,
      quantity_kg:  Number(swap.quantity_kg) || row.quantity_kg,
      raw_cost_usd: Number(swap.raw_cost_usd) || row.raw_cost_usd,
      __verified:   null,
      __swap_from:  row.ingredient,
      __swap_reason: swap.reason,
    });
    setSwapRow(null);
  };

  const machine = MACHINES.find((m) => m.value === machineKey) || MACHINES[1];

  const setMember = (id, patch) =>
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const addMember = () =>
    setMembers((prev) => [
      ...prev,
      { id: crypto.randomUUID(), name: `Member ${prev.length + 1}`, age: 30,
        gender: 'female', activity: 'mod', pregnant: false, breastfeeding: false },
    ]);
  const removeMember = (id) =>
    setMembers((prev) => prev.filter((m) => m.id !== id));

  // ------------------ Derived math ------------------
  const calc = useMemo(() => {
    const kcalPerDay = members.reduce((s, m) => s + kcalFor(m), 0);
    const totalKcal  = kcalPerDay * days;
    const totalMeals = members.length * mealsPerDay * days;

    const foodGroups = FOOD_GROUPS.map((g) => {
      const dailyKcal = kcalPerDay * g.pct;
      const dailyG    = (dailyKcal / 100) * g.gPer100Kcal;
      const totalG    = dailyG * days;
      return { ...g, dailyKcal, dailyG, totalKg: totalG / 1000 };
    });

    const totalFreshKg = foodGroups.reduce((s, g) => s + g.totalKg, 0);
    const batches      = Math.ceil(totalFreshKg / machine.capacityKg);
    const cycleDays    = machine.cycleH / 24;
    // Assume operator can run back-to-back cycles with ~1h changeover;
    // simplify by using cycleH per batch.
    const productionDays = batches * cycleDays;

    return {
      kcalPerDay,
      totalKcal,
      totalMeals,
      foodGroups,
      totalFreshKg,
      batches,
      productionDays,
    };
  }, [members, days, mealsPerDay, machine.capacityKg, machine.cycleH]);

  const generatePlan = async () => {
    if (!API) { toast.error('Sign in to use the AI planner.'); return; }
    setAiLoading(true);
    setPlan(null);
    setCurrentPlanId(null);
    try {
      const { data } = await axios.post(`${API}/tools/meal-plan-ai`, {
        household: members.map((m) => ({
          name: m.name, age: Number(m.age) || 30, gender: m.gender,
          activity: m.activity,
          pregnant: !!m.pregnant, breastfeeding: !!m.breastfeeding,
          kcal_per_day: kcalFor(m),
        })),
        coverage_days:       days,
        meals_per_day:       mealsPerDay,
        machine_capacity_kg: machine.capacityKg,
        machine_cycle_hours: machine.cycleH,
        pantry,
        preferences,
        currency,
        region,
      });
      setPlan(data);
      toast.success('Meal plan ready — scroll down for meals, shopping list, and production plan.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Meal plan failed');
    } finally { setAiLoading(false); }
  };

  // ------------------ Save / load / delete meal plan ------------------
  const saveCurrentPlan = async () => {
    if (!plan) { toast.error('Generate a plan first.'); return; }
    const name = saveName.trim() || `Plan · ${days}d · ${new Date().toLocaleDateString()}`;
    setSavingPlan(true);
    try {
      const { data } = await axios.post(`${API}/tools/meal-plan-ai/save`, {
        name,
        inputs: {
          members, days, mealsPerDay,
          machineKey, pantry, preferences,
          currency, region,
        },
        plan,
      });
      setCurrentPlanId(data.id);
      setSaveName('');
      toast.success(`Saved "${name}" — reload from My saved plans anytime.`);
      refreshSavedPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSavingPlan(false); }
  };

  const loadSavedPlan = async (id) => {
    try {
      const { data } = await axios.get(`${API}/tools/meal-plan-ai/saved/${id}`);
      const i = data.inputs || {};
      if (Array.isArray(i.members) && i.members.length) setMembers(i.members);
      if (i.days)         setDays(i.days);
      if (i.mealsPerDay)  setMealsPerDay(i.mealsPerDay);
      if (i.machineKey)   setMachineKey(i.machineKey);
      setPantry(i.pantry || '');
      setPreferences(i.preferences || '');
      setCurrency(i.currency || 'USD');
      setRegion(i.region || '');
      // Force React to remount the plan subtree by clearing before setting.
      // Without this the shopping list, calendar, and meals cards can hold
      // onto stale row references from the previous plan.
      setPlan(null);
      setCurrentPlanId(null);
      requestAnimationFrame(() => {
        setPlan(data.plan);
        setCurrentPlanId(data.id);
        toast.success(`Loaded "${data.name}"`);
        requestAnimationFrame(() => {
          document.querySelector('[data-testid="fp-plan-toolbar"]')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load failed');
    }
  };

  const deleteSavedPlan = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/tools/meal-plan-ai/saved/${id}`);
      if (currentPlanId === id) setCurrentPlanId(null);
      toast.success('Deleted.');
      refreshSavedPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div
      className={embedded ? 'space-y-6' : 'container mx-auto px-6 max-w-6xl space-y-6'}
      data-testid="food-prep-root"
    >
      {!embedded && <ToolNavBar activeSlug="food-prep" />}
      <ToolDisclaimer
        testid="fp-disclaimer"
        intro={
          <>
            <p className="font-semibold text-foreground">
              What this tool does
            </p>
            <p>
              The <span className="font-semibold">Home Meal Prep Calculator</span> helps
              a household plan a freeze-dry stockpile that actually feeds them. Start
              by adding each person with their age, gender, and activity level — the
              calculator looks up USDA daily calorie targets automatically. Pick a
              coverage window (days) and your freeze-dryer size, then use{' '}
              <span className="font-semibold">Master AI meal planner</span> to draft
              freeze-dry-friendly meals, an ingredient shopping list with
              Walmart-baseline prices, and a batch-by-batch production plan. Click any
              suggested meal to open a printable recipe card.
            </p>
          </>
        }
      />

      <div className="flex justify-end mb-1 no-print gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => window.print()}
          data-testid="fp-print"
        >
          <Printer className="w-4 h-4 mr-2" /> Print plan
        </Button>
        {user && <ToolFeedbackButton toolSlug="food-prep-resource" label="Send feedback" />}
      </div>

      {/* ------------------- Family roster ------------------- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="w-5 h-5 text-primary" /> Household roster
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fp-members-table">
              <thead className="bg-muted/60 text-left">
                <tr>
                  <th className="p-2 font-semibold">Name</th>
                  <th className="p-2 font-semibold w-20">Age</th>
                  <th className="p-2 font-semibold w-28">Gender</th>
                  <th className="p-2 font-semibold w-44">Activity</th>
                  <th className="p-2 font-semibold w-40">Life stage</th>
                  <th className="p-2 font-semibold w-28 text-right">Kcal / day</th>
                  <th className="p-2 no-print w-10"></th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-t border-border align-middle">
                    <td className="p-2">
                      <Input
                        value={m.name}
                        onChange={(e) => setMember(m.id, { name: e.target.value })}
                        className="h-9"
                        data-testid={`fp-m-name-${m.id}`}
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        min="1"
                        max="120"
                        value={m.age}
                        onChange={(e) => setMember(m.id, { age: Number(e.target.value) })}
                        className="h-9"
                        data-testid={`fp-m-age-${m.id}`}
                      />
                    </td>
                    <td className="p-2">
                      <select
                        value={m.gender}
                        onChange={(e) => setMember(m.id, { gender: e.target.value, pregnant: false, breastfeeding: false })}
                        className="h-9 w-full px-2 rounded-md border border-input bg-background"
                        data-testid={`fp-m-gender-${m.id}`}
                      >
                        <option value="female">Female</option>
                        <option value="male">Male</option>
                      </select>
                    </td>
                    <td className="p-2">
                      <select
                        value={m.activity}
                        onChange={(e) => setMember(m.id, { activity: e.target.value })}
                        className="h-9 w-full px-2 rounded-md border border-input bg-background"
                        data-testid={`fp-m-activity-${m.id}`}
                      >
                        {ACTIVITY_LEVELS.map((a) => (
                          <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-2">
                      {m.gender === 'female' && Number(m.age) >= 14 && Number(m.age) <= 55 ? (
                        <select
                          value={m.pregnant ? 'preg' : m.breastfeeding ? 'bf' : 'none'}
                          onChange={(e) => setMember(m.id, {
                            pregnant:      e.target.value === 'preg',
                            breastfeeding: e.target.value === 'bf',
                          })}
                          className="h-9 w-full px-2 rounded-md border border-input bg-background"
                          data-testid={`fp-m-lifestage-${m.id}`}
                        >
                          <option value="none">—</option>
                          <option value="preg">Pregnant (+{PREGNANCY_ADD} kcal)</option>
                          <option value="bf">Breastfeeding (+{BREASTFEEDING_ADD} kcal)</option>
                        </select>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="p-2 text-right font-mono font-semibold" data-testid={`fp-m-kcal-${m.id}`}>
                      {kcalFor(m).toLocaleString()}
                    </td>
                    <td className="p-2 no-print">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeMember(m.id)}
                        disabled={members.length <= 1}
                        aria-label="Remove household member"
                        data-testid={`fp-m-remove-${m.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center pt-1 no-print">
            <Button type="button" variant="outline" size="sm" onClick={addMember} data-testid="fp-add-member">
              <Plus className="w-4 h-4 mr-2" /> Add household member
            </Button>
            <div className="text-sm">
              <span className="text-muted-foreground">Household total: </span>
              <span className="font-bold text-primary" data-testid="fp-total-kcal-day">
                {calc.kcalPerDay.toLocaleString()} kcal / day
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ------------------- Timeline + machine picker ------------------- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="w-5 h-5 text-primary" /> Coverage window & equipment
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Coverage (days)</Label>
            <Input
              type="number"
              min="1"
              max="730"
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(730, Number(e.target.value) || 1)))}
              className="mt-1 h-10"
              data-testid="fp-days"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              {days} days · ≈ {(days / 30).toFixed(1)} months
            </div>
          </div>
          <div>
            <Label className="text-xs">Meals per person per day</Label>
            <select
              value={mealsPerDay}
              onChange={(e) => setMealsPerDay(Number(e.target.value))}
              className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              data-testid="fp-meals-per-day"
            >
              {[2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>{n} meals</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Freeze-dryer size</Label>
            <select
              value={machineKey}
              onChange={(e) => setMachineKey(e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              data-testid="fp-machine"
            >
              {MACHINES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* ------------------- Results ------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="fp-summary-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Utensils className="w-5 h-5 text-primary" /> Household plan
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <SummaryRow label="Total calories over window" value={`${calc.totalKcal.toLocaleString()} kcal`} testid="fp-total-kcal" />
            <SummaryRow label="Total meals to produce"     value={calc.totalMeals.toLocaleString()}          testid="fp-total-meals" />
            <SummaryRow label="Total fresh food to source" value={`${calc.totalFreshKg.toFixed(1)} kg`}      testid="fp-total-fresh" />
            <SummaryRow label="≈ Freeze-dried (dry) weight" value={`${(calc.totalFreshKg * 0.20).toFixed(1)} kg`} testid="fp-total-dry" />
            <SummaryRow label="Batches needed"             value={calc.batches.toLocaleString()}             testid="fp-batches" />
            <SummaryRow
              label="Continuous production time"
              value={
                calc.productionDays >= 30
                  ? `${(calc.productionDays / 30).toFixed(1)} months`
                  : `${calc.productionDays.toFixed(1)} days`
              }
              testid="fp-production-days"
            />
            <div
              className="text-xs text-muted-foreground border-t border-border pt-2 mt-2 flex gap-2"
              data-testid="fp-plan-note"
            >
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600" />
              <span>
                Estimates assume back-to-back cycles on one machine. Real production time
                often runs 20-30% longer once loading, pre-freeze, and packaging are counted.
                Dry weight assumes ~80% moisture loss (typical mixed-diet freeze-dry).
              </span>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="fp-groups-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Package className="w-5 h-5 text-primary" /> Food group breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-left">
                <tr>
                  <th className="p-2 font-semibold">Group</th>
                  <th className="p-2 font-semibold text-right">Kcal / day</th>
                  <th className="p-2 font-semibold text-right">Fresh / day</th>
                  <th className="p-2 font-semibold text-right">Total (window)</th>
                </tr>
              </thead>
              <tbody>
                {calc.foodGroups.map((g) => (
                  <tr key={g.key} className="border-t border-border" data-testid={`fp-group-${g.key}`}>
                    <td className="p-2 font-semibold">{g.label}</td>
                    <td className="p-2 text-right font-mono">{Math.round(g.dailyKcal).toLocaleString()}</td>
                    <td className="p-2 text-right font-mono">{Math.round(g.dailyG).toLocaleString()} g</td>
                    <td className="p-2 text-right font-mono">{g.totalKg.toFixed(1)} kg</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-muted-foreground mt-3">
              Group ratios follow USDA MyPlate defaults. Adjust when planning special diets
              (keto shifts protein up ~10 pts, plant-based swaps protein for grains/legumes).
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ------------------- Master AI Planner ------------------- */}
      <Card data-testid="fp-ai-planner">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ChefHat className="w-5 h-5 text-primary" /> Master AI meal planner
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Tell the AI what you already have, any dietary constraints, and any
            meals your family loves. We'll design freeze-dry-friendly meals,
            build a shopping list with cost estimates, and lay out a batch-by-batch
            production plan.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Currency</Label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="fp-currency"
              >
                <option value="USD">USD ($) — Walmart US</option>
                <option value="CAD">CAD (C$) — Walmart Canada</option>
              </select>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Region for pricing
              </Label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="fp-region"
              >
                <option value="">Continental average</option>
                <optgroup label="United States">
                  {['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </optgroup>
                <optgroup label="Canada">
                  {['Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador','Nova Scotia','Northwest Territories','Nunavut','Ontario','Prince Edward Island','Quebec','Saskatchewan','Yukon'].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">What you already have (pantry)</Label>
              <Textarea
                rows={3}
                value={pantry}
                onChange={(e) => setPantry(e.target.value)}
                placeholder="e.g. 20 lb rice, canned tomatoes, 5 lb dried beans, frozen mixed veg"
                data-testid="fp-pantry"
              />
            </div>
            <div>
              <Label className="text-xs">Preferences / constraints</Label>
              <Textarea
                rows={3}
                value={preferences}
                onChange={(e) => setPreferences(e.target.value)}
                placeholder="Allergies, diet (keto, plant-based), meals you love, meals to avoid"
                data-testid="fp-preferences"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Input
              type="text"
              value={region + ' — nearby city (optional)' === '' ? '' : undefined}
              className="hidden"
              aria-hidden
            />
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={generatePlan}
              disabled={aiLoading || members.length === 0}
              data-testid="fp-generate-plan"
            >
              {aiLoading
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Sparkles className="w-4 h-4 mr-2" />}
              Generate my meal plan
            </Button>
          </div>
          <AIProgressIndicator
            active={aiLoading}
            estimatedMs={22000}
            data-testid="fp-ai-progress"
            steps={[
              { at: 0,     label: 'Reviewing household roster and pantry…' },
              { at: 4000,  label: 'Selecting freeze-dry-friendly meals…' },
              { at: 10000, label: 'Building shopping list + cost estimates…' },
              { at: 16000, label: 'Scheduling production plan…' },
            ]}
          />
        </CardContent>
      </Card>

      {/* ------------------- My saved plans (owner only) ------------------- */}
      {user && savedPlans.length > 0 && (
        <Card data-testid="fp-saved-plans-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FolderOpen className="w-5 h-5 text-primary" /> My saved plans ({savedPlans.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {savedPlans.map((sp) => (
                <li
                  key={sp.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-border p-2 hover:border-primary/60 transition-colors"
                  data-testid={`fp-saved-${sp.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{sp.name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      Saved {new Date(sp.updated_at || sp.created_at).toLocaleDateString()}
                      {sp.inputs?.days ? ` · ${sp.inputs.days} days` : ''}
                      {sp.inputs?.currency ? ` · ${sp.inputs.currency}` : ''}
                    </div>
                  </div>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => loadSavedPlan(sp.id)}
                    data-testid={`fp-load-${sp.id}`}
                  >
                    <FolderOpen className="w-3.5 h-3.5 mr-1" /> Load
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => deleteSavedPlan(sp.id, sp.name)}
                    data-testid={`fp-delete-${sp.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* ------------------- AI plan output ------------------- */}
      {plan && (
        <>
          <div className="flex flex-wrap gap-2 justify-end no-print" data-testid="fp-plan-toolbar">
            <div className="flex items-center gap-2 mr-auto">
              <Input
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder={`Name this plan (e.g. Family stockpile — ${days} days)`}
                className="w-64"
                data-testid="fp-save-name"
              />
              <Button
                type="button" size="sm"
                onClick={saveCurrentPlan}
                disabled={savingPlan}
                data-testid="fp-save-plan"
              >
                {savingPlan
                  ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  : <Save className="w-3.5 h-3.5 mr-1" />}
                {currentPlanId ? 'Save as new' : 'Save plan'}
              </Button>
            </div>
            <PlanBundlePdf
              plan={plan}
              members={membersForPdf}
              days={days}
              mealsPerDay={mealsPerDay}
              machine={machine}
              calc={calc}
            />
            <PlanSectionPdfButtons
              plan={plan}
              members={membersForPdf}
              days={days}
              mealsPerDay={mealsPerDay}
              machine={machine}
              calc={calc}
            />
            <MealPlanBooklet plan={plan} days={days} machine={machine} />
          </div>

          {plan.narrative && (
            <Card data-testid="fp-plan-narrative">
              <CardContent className="p-4">
                <p className="text-sm text-foreground/90">{plan.narrative}</p>
                {plan.disclaimer && (
                  <p className="text-[11px] text-muted-foreground mt-2 flex gap-1 items-start">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0 text-amber-600" />
                    {plan.disclaimer}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {plan.totals && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="fp-totals">
              <TotalTile label="Raw ingredients" value={`$${plan.totals.raw_cost_usd?.toLocaleString()}`} icon={ShoppingCart} testid="fp-total-raw" />
              <TotalTile label="Ready-to-eat equivalent" value={`$${plan.totals.ready_meal_cost_usd?.toLocaleString()}`} icon={DollarSign} testid="fp-total-rte" />
              <TotalTile label="Estimated savings" value={`$${plan.totals.savings_usd?.toLocaleString()}`} icon={DollarSign} tone="green" testid="fp-total-savings" />
              <TotalTile label="Savings %" value={`${plan.totals.savings_pct}%`} icon={Sparkles} tone="green" testid="fp-total-savings-pct" />
            </div>
          )}

          {plan.meals?.length > 0 && (
            <Card data-testid="fp-meals-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ChefHat className="w-5 h-5 text-primary" /> Suggested meals ({plan.meals.length})
                  <span className="text-xs text-muted-foreground ml-2 font-normal">
                    Click a row for a printable recipe card
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-left">
                    <tr>
                      <th className="p-2 font-semibold">Meal</th>
                      <th className="p-2 font-semibold text-right w-20">Servings</th>
                      <th className="p-2 font-semibold text-right w-24">Kcal / svg</th>
                      <th className="p-2 font-semibold text-right w-24">Protein</th>
                      <th className="p-2 font-semibold">Key ingredients</th>
                      <th className="p-2 font-semibold">Freeze-dry notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.meals.map((m, i) => (
                      <tr
                        key={i}
                        className="border-t border-border align-top hover:bg-accent/40 cursor-pointer transition-colors"
                        data-testid={`fp-meal-${i}`}
                        onClick={() => setActiveMeal(m)}
                      >
                        <td className="p-2 font-semibold whitespace-nowrap text-primary underline decoration-dotted">
                          {m.name}
                        </td>
                        <td className="p-2 text-right font-mono">{m.servings}</td>
                        <td className="p-2 text-right font-mono">{m.kcal_per_serving}</td>
                        <td className="p-2 text-right font-mono">{m.protein_g} g</td>
                        <td className="p-2 text-xs text-muted-foreground">{(m.key_ingredients || []).join(', ')}</td>
                        <td className="p-2 text-xs text-muted-foreground">{m.freeze_dry_notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {plan.shopping_list?.length > 0 && (
            <Card data-testid="fp-shopping-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ShoppingCart className="w-5 h-5 text-primary" /> Shopping list ({plan.shopping_list.length})
                </CardTitle>
                <div className="flex gap-2 no-print flex-wrap justify-end">
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => {
                      const rows = [
                        ['Ingredient', 'Quantity (kg wet)', 'Raw cost USD', 'Ready-to-eat equiv USD', 'Verified pack', 'Source'],
                        ...plan.shopping_list.map((it) => [
                          it.ingredient,
                          Number(it.quantity_kg || 0).toFixed(2),
                          Number(it.raw_cost_usd || 0).toFixed(2),
                          Number(it.ready_meal_equivalent_usd || 0).toFixed(2),
                          it.__verified?.product_name || '',
                          it.__verified?.sources?.[0] || '',
                        ]),
                      ];
                      const csv = rows.map((r) => r.map((c) => {
                        const s = String(c ?? '');
                        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                      }).join(',')).join('\n');
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                      const url  = URL.createObjectURL(blob);
                      const a    = document.createElement('a');
                      a.href = url;
                      a.download = 'shopping-list.csv';
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                    }}
                    data-testid="fp-shop-download"
                  >
                    <FileText className="w-3.5 h-3.5 mr-1" /> CSV
                  </Button>
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => window.print()}
                    data-testid="fp-shop-print"
                  >
                    <Printer className="w-3.5 h-3.5 mr-1" /> Print
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <div className="rounded-md border border-blue-500/40 bg-blue-500/5 p-3 mb-3 text-[11px] text-foreground/80 leading-relaxed" data-testid="fp-walmart-baseline">
                  <div className="font-bold uppercase tracking-wider text-blue-700 dark:text-blue-400 text-[10px] mb-1">
                    About the Walmart baseline
                  </div>
                  Walmart is used as the pricing baseline because of its accessibility
                  to communities throughout North America. It is a guideline only —
                  users ultimately determine where they shop. Walmart prices are
                  subject to change without notice, and{' '}
                  <span className="font-semibold">Freeze Dry Academy is not
                  responsible for third-party data</span>. Verify locally before
                  purchasing.
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-left">
                    <tr>
                      <th className="p-2 font-semibold">Ingredient</th>
                      <th className="p-2 font-semibold text-right w-28">Qty (kg wet)</th>
                      <th className="p-2 font-semibold text-right w-28">Raw cost</th>
                      <th className="p-2 font-semibold text-right w-36">Ready-to-eat equiv.</th>
                      <th className="p-2 font-semibold w-56 no-print">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.shopping_list.map((it, i) => (
                      <tr key={i} className="border-t border-border align-top" data-testid={`fp-shop-${i}`}>
                        <td className="p-2 font-semibold">
                          {it.ingredient}
                          {it.__swap_from && (
                            <div className="text-[10px] text-primary font-normal mt-0.5 flex items-center gap-1">
                              <Shuffle className="w-3 h-3" /> swapped from {it.__swap_from}
                            </div>
                          )}
                          {it.ready_meal_reference ? (
                            <div className="text-[10px] text-muted-foreground font-normal italic mt-0.5">
                              vs. {it.ready_meal_reference}
                            </div>
                          ) : null}
                          {it.__verified && (
                            <div className="mt-1 rounded border border-green-500/40 bg-green-500/5 p-1.5 text-[10px] font-normal text-green-800 dark:text-green-300">
                              <div className="flex items-center gap-1 font-semibold">
                                <CheckCircle2 className="w-3 h-3" /> Verified live
                                <span className="text-muted-foreground ml-auto">
                                  ${Number(it.__verified.price_per_kg).toFixed(2)}/kg
                                </span>
                              </div>
                              <div className="text-muted-foreground">
                                {it.__verified.product_name}
                                {it.__verified.unit_size ? ` · ${it.__verified.unit_size}` : ''}
                              </div>
                              {it.__verified.sources?.[0] && (
                                <a
                                  href={it.__verified.sources[0]}
                                  target="_blank" rel="noopener noreferrer"
                                  className="text-primary hover:underline break-all inline-flex items-center gap-1"
                                  data-testid={`fp-shop-${i}-cite`}
                                >
                                  <ExternalLink className="w-2.5 h-2.5" />
                                  {new URL(it.__verified.sources[0]).hostname.replace('www.', '')}
                                </a>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-right font-mono">{Number(it.quantity_kg || 0).toFixed(1)}</td>
                        <td className="p-2 text-right font-mono">${Number(it.raw_cost_usd || 0).toFixed(2)}</td>
                        <td className="p-2 text-right font-mono text-muted-foreground">${Number(it.ready_meal_equivalent_usd || 0).toFixed(2)}</td>
                        <td className="p-2 no-print">
                          <div className="flex gap-1 flex-wrap">
                            <Button
                              type="button" size="sm" variant="outline"
                              onClick={() => verifyRowPrice(i)}
                              disabled={verifyingIdx === i}
                              data-testid={`fp-shop-verify-${i}`}
                              title="Look up live Walmart price"
                            >
                              {verifyingIdx === i
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <RefreshCw className="w-3 h-3" />}
                              <span className="ml-1 text-[10px]">
                                {it.__verified ? 'Refresh' : 'Verify'}
                              </span>
                            </Button>
                            <Button
                              type="button" size="sm" variant="outline"
                              onClick={() => setSwapRow({ ...it, __rowIndex: i })}
                              data-testid={`fp-shop-swap-${i}`}
                              title="AI-suggested substitutes"
                            >
                              <Shuffle className="w-3 h-3" />
                              <span className="ml-1 text-[10px]">Swap</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Pricing baseline: Walmart Great Value / US store-brand retail (2024-2025). Regional prices vary ±20%.
                </p>
                {plan.sources?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border text-[11px] text-muted-foreground space-y-1" data-testid="fp-sources">
                    <div className="font-semibold uppercase tracking-wider">Sources consulted:</div>
                    {plan.sources.map((u, i) => (
                      <a
                        key={i}
                        href={u}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-primary hover:underline break-all"
                        data-testid={`fp-source-${i}`}
                      >
                        {u}
                      </a>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {plan.production_plan?.schedule?.length > 0 && (
            <Card data-testid="fp-production-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="w-5 h-5 text-primary" /> Production plan · {plan.production_plan.batches} batches over {plan.production_plan.weeks} weeks
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ProductionCalendar
                  batches={plan.production_plan.batches}
                  cycle_hours={machine.cycleH}
                  start_date={new Date().toISOString().slice(0, 10)}
                  labels={(plan.meals || []).map((m) => m.name)}
                />
                <details className="text-xs text-muted-foreground" data-testid="fp-schedule-lines">
                  <summary className="cursor-pointer font-semibold hover:text-foreground">
                    Show weekly narrative
                  </summary>
                  <ol className="mt-2 space-y-1">
                    {plan.production_plan.schedule.map((line, i) => (
                      <li key={i} className="flex gap-2" data-testid={`fp-week-${i}`}>
                        <span className="text-primary font-bold">→</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ol>
                </details>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <RecipeCardModal
        meal={activeMeal}
        open={!!activeMeal}
        onClose={() => setActiveMeal(null)}
      />

      <SubstitutionModal
        row={swapRow}
        onClose={() => setSwapRow(null)}
        onApply={(swap) => applySwap(swapRow?.__rowIndex, swap)}
      />

      <div className="text-xs text-muted-foreground flex items-start gap-2 no-print">
        <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" />
        <span>
          Calorie targets sourced from the <a
            href="https://www.dietaryguidelines.gov/sites/default/files/2021-03/Dietary_Guidelines_for_Americans-2020-2025.pdf"
            target="_blank" rel="noopener noreferrer" className="text-primary hover:underline"
          >USDA Dietary Guidelines 2020-2025 (Appendix 2)</a>. Individual needs vary — consult
          a dietitian for special medical conditions.
        </span>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, testid }) {
  return (
    <div className="flex justify-between items-baseline text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-bold text-foreground" data-testid={testid}>{value}</span>
    </div>
  );
}

function TotalTile({ label, value, icon: Icon, tone, testid }) {
  const toneCls = tone === 'green'
    ? 'text-green-600 border-green-500/40 bg-green-500/5'
    : 'text-foreground border-border bg-background';
  return (
    <div className={`rounded-lg border p-3 ${toneCls}`}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-xl font-extrabold mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}
